<template>
  <div class="comparison">
    <div class="filter">
      <form class="form-inline">
        <div class="form-group select-file">
          <input type="file" @change="handleFileUpload" accept=".fit,.tcx" multiple class="form-control-file form-control-sm" />
        </div>
        <div class="form-group select-filter">
          <label class="select-text" for="field-select">选择对比字段：</label>
          <select id="field-select" v-model="selectedField" class="form-control form-control-sm">
            <option value="speed">速度 (米/秒)</option>
            <option value="heart_rate">心率 (次/分)</option>
            <option value="cadence">步频 (次/分)</option>
            <option value="step_length">步幅 (米)</option>
          </select>
        </div>
        <button type="button" @click="startComparison" :disabled="filesData.length < 2" class="btn btn-primary mb-2 btn-sm">开始对比</button>
      </form>
    </div>
    <div class="chart">
      <div ref="chart" class="chart-box"></div>
    </div>
  </div>
</template>

<script>
import FitFileParser from "fit-file-parser";
import * as echarts from "echarts";
import xml2js from "xml2js";

export default {
  name: "FileUploader",
  data() {
    return {
      filesData: [], // 存储解析后的文件数据
      selectedField: "speed", // 默认对比字段
    };
  },
  mounted() {
    window.addEventListener("resize", this.resizeChart);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeChart);
  },
  methods: {
    handleFileUpload(event) {
      const files = Array.from(event.target.files);
      if (files.length > 10) {
        alert("最多只能上传 10 个文件");
        return;
      }
      this.filesData = [];
      files.forEach((file) => {
        const fileName = file.name.toLowerCase();
        if (fileName.endsWith(".fit")) {
          this.parseFitFile(file);
        } else if (fileName.endsWith(".tcx")) {
          this.parseTcxFile(file);
        } else {
          alert(`${file.name} 不是有效的 .fit 或 .tcx 文件`);
        }
      });
    },
    parseFitFile(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const fitData = e.target.result;
        const parser = new FitFileParser();
        parser.parse(fitData, (error, data) => {
          if (error) {
            alert(`解析文件 ${file.name} 失败`);
            console.error(`解析 ${file.name} 失败:`, error);
          } else {
            const records = data.records || [];
            const cleanedData = this.cleanFitData(records);
            this.filesData.push({ name: file.name, data: cleanedData });
          }
        });
      };
      reader.readAsArrayBuffer(file);
    },
    parseTcxFile(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const tcxData = e.target.result;
        const parser = new xml2js.Parser({ explicitArray: false, mergeAttrs: true });

        parser.parseString(tcxData, (error, result) => {
          if (error) {
            alert(`解析文件 ${file.name} 失败`);
            console.error(`解析 ${file.name} 失败:`, error);
          } else {
            const activity = result?.TrainingCenterDatabase?.Activities?.Activity;
            if (!activity) {
              alert(`文件 ${file.name} 中没有有效的活动数据`);
              return;
            }
            const laps = Array.isArray(activity.Lap) ? activity.Lap : [activity.Lap];
            let trackpoints = [];
            laps.forEach((lap) => {
              if (lap.Track && lap.Track.Trackpoint) {
                trackpoints = trackpoints.concat(
                  Array.isArray(lap.Track.Trackpoint)
                    ? lap.Track.Trackpoint
                    : [lap.Track.Trackpoint]
                );
              }
            });

            if (trackpoints.length === 0) {
              alert(`文件 ${file.name} 中没有有效的 Trackpoint 数据`);
              return;
            }

            const cleanedData = this.cleanTcxData(trackpoints);
            this.filesData.push({ name: file.name, data: cleanedData });
          }
        });
      };
      reader.readAsText(file);
    },
    cleanFitData(records) {
      return records.map((record) => ({
        timestamp: this.convertToBeijingTime(record.timestamp),
        speed: record.enhanced_speed || record.speed || 0,
        heart_rate: record.heart_rate || 0,
        cadence: record.cadence ? record.cadence * 2 : 0,
        step_length: record.step_length ? (record.step_length / 1000).toFixed(3) : 0,
      }));
    },
    cleanTcxData(trackpoints) {
      return trackpoints.map((trackpoint) => ({
        timestamp: this.convertToBeijingTime(trackpoint.Time),
        speed: 0,
        heart_rate: trackpoint.HeartRateBpm?.Value || 0,
        cadence: trackpoint.Cadence ? trackpoint.Cadence * 2 : 0,
        step_length: 0,
      }));
    },
    convertToBeijingTime(timestamp) {
      if (!timestamp) return "未知时间";
      const date = new Date(timestamp);
      if (isNaN(date)) {
        console.error("Invalid timestamp:", timestamp);
        return "无效时间";
      }
      date.setUTCHours(date.getUTCHours() + 8);
      return date.toISOString().replace("T", " ").split(".")[0];
    },
    startComparison() {
      if (this.filesData.length < 2) {
        alert("请至少上传 2 个文件");
        return;
      }

      this.filesData.sort((a, b) => {
        const dateA = new Date(a.data[0]?.timestamp || 0);
        const dateB = new Date(b.data[0]?.timestamp || 0);
        return dateB - dateA;
      });

      this.renderChart();
    },
    renderChart() {
      const series = [];
      const timeStamps = this.filesData[0].data.map((record) => record.timestamp);
      this.filesData.forEach((file) => {
        series.push({
          name: file.name,
          type: "line",
          data: file.data.map((record) => record[this.selectedField]),
          smooth: true,
        });
      });

      this.$nextTick(() => {
        const chartContainer = this.$refs.chart;
        const chart = echarts.init(chartContainer);
        chart.setOption({
          backgroundColor: "#fff",
          tooltip: { trigger: "axis", axisPointer: { type: "cross" } },
          legend: { data: series.map((s) => s.name) },
          xAxis: { type: "category", boundaryGap: false, data: timeStamps },
          yAxis: {
            type: "value",
            name:
              this.selectedField === "speed"
                ? "速度 (米/秒)"
                : this.selectedField === "heart_rate"
                ? "心率 (次/分)"
                : this.selectedField === "cadence"
                ? "步频 (次/分)"
                : "步幅 (米)",
          },
          series,
          dataZoom: [
            { type: "inside", xAxisIndex: [0], start: 0, end: 100 },
            { type: "slider", xAxisIndex: [0], start: 0, end: 100 },
          ],
        });
      });
    },
    resizeChart() {
      const chartContainer = this.$refs.chart;
      if (chartContainer) {
        const chart = echarts.getInstanceByDom(chartContainer);
        if (chart) chart.resize();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.comparison {padding:20px 0 0;}
.filter {width:100%; max-width:$main-width; margin:auto; padding-bottom:20px;}
.form-group {margin-right:20px;}
.btn {margin-bottom:0!important;}
.chart {width:100%; height:calc(100vh - 160px); background:#f2f2f2; position:relative;
  &::before {
    content:'数据对比图表展示区域';
    display:block;
    position:absolute;
    top:50%;
    left:50%;
    transform: translate(-50%, -50%);
    color:#999;
    font-size:36px;
  }
  .chart-box {width:100%; height:100%;}
}
 @media screen and (max-width: 768px) {
  .comparison {padding:10px 0 0;}
  .select-file {margin:0 0 10px;}
  .select-text {display:none;}
  .select-filter {margin:0 10px 0 8px;}
  .filter {padding-bottom:10px;}
  .chart {height:calc(100vh - 142px);
    &::before {
      font-size:18px;
    }
  }
 }
</style>
